import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, FileText, GraduationCap, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { levelStyle } from "@/lib/constants";
import DerouleSheet from "@/components/DerouleSheet";

const LEVELS = ["3e", "4e", "5e", "6e"];

export default function AnimatorDeroules() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "animateur")) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.role === "animateur") api.get("/animator/deroules").then((r) => setList(r.data)).catch(() => {});
  }, [user]);

  if (!user || user.role !== "animateur") return null;

  const open = async (slug) => {
    setBusy(true);
    try {
      const r = await api.get(`/animator/deroules/${slug}`);
      setSelected(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error("Chargement du déroulé impossible. Réessayez."); }
    finally { setBusy(false); }
  };

  if (selected) {
    return (
      <div className="py-6">
        <div className="no-print max-w-5xl mx-auto px-4 sm:px-6">
          <button onClick={() => setSelected(null)} data-testid="deroule-back" className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour à la liste
          </button>
        </div>
        <DerouleSheet deroule={selected} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="animator-deroules">
      <button onClick={() => navigate("/espace-animateur")} className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors mb-6">
        <ChevronLeft className="w-4 h-4" /> Espace animateur
      </button>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Espace animateur</span>
        <h1 className="font-heading font-black text-4xl text-slate-900 mt-1">Fiches de déroulé des séances</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Le script complet de chaque atelier : mission, déroulé minuté, paroles à dire, gestion des imprévus,
          grille d'évaluation et bonus. Cliquez sur une fiche pour l'ouvrir, puis « Imprimer / PDF » si besoin.
        </p>
      </motion.div>

      {LEVELS.map((lvl) => {
        const items = list.filter((d) => d.level === lvl).sort((a, b) => (a.seance || 0) - (b.seance || 0));
        if (items.length === 0) return null;
        const s = levelStyle(lvl);
        return (
          <div key={lvl} className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className={`inline-flex items-center gap-1.5 text-sm font-black px-3 py-1 rounded-full ${s.bg} ${s.text}`}>
                <GraduationCap className="w-4 h-4" /> {s.label}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {items.map((d) => (
                <button
                  key={d.slug}
                  onClick={() => open(d.slug)}
                  disabled={busy}
                  data-testid={`deroule-card-${d.slug}`}
                  className="text-left rounded-3xl border border-slate-200 bg-white p-5 hover:border-slate-900 hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50"
                >
                  <span className="grid place-items-center w-10 h-10 rounded-2xl bg-slate-900 text-white mb-3"><FileText className="w-5 h-5" /></span>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Séance {d.seance}</p>
                  <p className="font-heading font-extrabold text-slate-900 leading-tight mt-0.5">{d.title}</p>
                  <p className="text-xs text-slate-500 mt-2">{d.referentiel}</p>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
